import { Input } from "@ember/component";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import DEditor from "discourse/components/d-editor";
import PickFilesButton from "discourse/components/pick-files-button";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import DecEventPreviewPopover from "../components/dec-event-preview-popover";
import dismissMenu from "../modifiers/dec-dismiss-menu";
import dismissOnOutside from "../modifiers/dec-dismiss-on-outside";

export default <template>
  <div
    class="discourse-events-calendar"
    {{didUpdate @controller.ensureEventsForCurrentQueryParams @controller.nonSearchEventsStateSignature}}
  >
    <header class="discourse-events-calendar__header discourse-events-calendar__header--calendar">
      <div>
        <h1>Calendar</h1>
        <p>Upcoming community events.</p>
      </div>

      <div class="discourse-events-calendar__header-actions">
        <details class="discourse-events-calendar__subscribe-menu" {{dismissMenu}}>
          <summary class="btn btn-default discourse-events-calendar__subscribe-summary">
            {{dIcon "calendar-days"}}
            <span>Subscribe</span>
            {{dIcon "angle-down"}}
          </summary>

          <div class="discourse-events-calendar__subscribe-menu-popover">
            <a
              href={{@controller.subscribeUrl}}
              class="btn btn-default"
              title={{@controller.subscribeTitle}}
              data-auto-route="false"
              download="events.ics"
            >
              {{dIcon "download"}}
              <span>Download .ics feed</span>
            </a>

            {{#if @controller.canUsePersonalFilters}}
              <button
                type="button"
                class={{@controller.subscribeViewButtonClasses}}
                disabled={{@controller.isSavingSubscription}}
                {{on "click" @controller.createSubscriptionForCurrentView}}
              >
                {{dIcon "bell"}}
                <span>
                  {{#if @controller.isSavingSubscription}}
                    Saving...
                  {{else}}
                    Subscribe to this view
                  {{/if}}
                </span>
              </button>

              {{#if @controller.subscriptionManagementUrl}}
                <a
                  href={{@controller.subscriptionManagementUrl}}
                  class="btn btn-default"
                >
                  {{dIcon "calendar-days"}}
                  <span>Manage subscriptions</span>
                </a>
              {{/if}}
            {{/if}}

            {{#if @controller.canUseExternalCalendars}}
              <button
                type="button"
                class="btn btn-default"
                aria-expanded={{@controller.showExternalCalendarPanel}}
                {{on "click" @controller.toggleExternalCalendarPanel}}
              >
                {{dIcon "calendar-plus"}}
                <span>External calendars</span>
              </button>
            {{/if}}
          </div>
        </details>

        {{#if @controller.showHeaderCreateButton}}
          <button
            type="button"
            class="btn btn-primary discourse-events-calendar__header-create"
            aria-expanded={{@controller.isCreateFormExpanded}}
            {{on "click" @controller.toggleCreateForm}}
          >
            {{@controller.createFormToggleLabel}}
          </button>
        {{/if}}
      </div>

      <details class="discourse-events-calendar__mobile-header-menu" {{dismissMenu}}>
        <summary aria-label="Calendar actions">
          <span></span>
          <span></span>
          <span></span>
        </summary>

        <div class="discourse-events-calendar__mobile-header-menu-popover">
          <a
            href={{@controller.subscribeUrl}}
            class="btn btn-default"
            title={{@controller.subscribeTitle}}
            data-auto-route="false"
            download="events.ics"
          >
            Subscribe
          </a>

          {{#if @controller.canUsePersonalFilters}}
            <button
              type="button"
              class={{@controller.subscribeViewButtonClasses}}
              disabled={{@controller.isSavingSubscription}}
              {{on "click" @controller.createSubscriptionForCurrentView}}
            >
              Subscribe view
            </button>

            {{#if @controller.subscriptionManagementUrl}}
              <a href={{@controller.subscriptionManagementUrl}} class="btn">
                Manage subscriptions
              </a>
            {{/if}}
          {{/if}}

          {{#if @controller.canUseExternalCalendars}}
            <button
              type="button"
              class="btn"
              aria-expanded={{@controller.showExternalCalendarPanel}}
              {{on "click" @controller.toggleExternalCalendarPanel}}
            >
              External calendars
            </button>
          {{/if}}

          {{#if @controller.canCreateEvents}}
            <button
              type="button"
              class="btn"
              {{on "click" @controller.openCreateEventFromMenu}}
            >
              New event
            </button>
          {{/if}}

        </div>
      </details>
    </header>

    {{#if @controller.subscriptionError}}
      <div class="discourse-events-calendar__error" role="alert">
        {{@controller.subscriptionError}}
      </div>
    {{/if}}

    {{#if @controller.subscriptionLinks}}
      <div class="discourse-events-calendar__notice" role="status">
        <strong>{{@controller.subscriptionNotice}}</strong>
        <button type="button" class="btn btn-small" {{on "click" @controller.copySubscriptionUrl}}>
          Copy URL
        </button>
        <a
          href={{@controller.subscriptionLinks.google_url}}
          class="btn btn-small"
          target="_blank"
          rel="noopener noreferrer"
        >
          Add to Google
        </a>
        <a
          href={{@controller.subscriptionLinks.apple_url}}
          class="btn btn-small"
          data-auto-route="false"
        >
          Add to Apple
        </a>
        <a
          href={{@controller.subscriptionLinks.outlook_url}}
          class="btn btn-small"
          target="_blank"
          rel="noopener noreferrer"
        >
          Add to Outlook
        </a>
        {{#if @controller.subscriptionManagementUrl}}
          <a
            href={{@controller.subscriptionManagementUrl}}
            class="btn btn-small"
          >
            Manage subscriptions
          </a>
        {{/if}}
        <button
          type="button"
          class="btn btn-small btn-icon"
          title="Dismiss"
          aria-label="Dismiss subscription notice"
          {{on "click" @controller.dismissSubscriptionNotice}}
        >
          {{dIcon "x"}}
        </button>
      </div>
    {{else}}
      {{#if @controller.subscriptionNotice}}
        <div class="discourse-events-calendar__notice" role="status">
          {{@controller.subscriptionNotice}}
          <button
            type="button"
            class="btn btn-small btn-icon"
            title="Dismiss"
            aria-label="Dismiss subscription notice"
            {{on "click" @controller.dismissSubscriptionNotice}}
          >
            {{dIcon "x"}}
          </button>
        </div>
      {{/if}}
    {{/if}}

    {{#if @controller.showExternalCalendarPanel}}
      <section
        class="discourse-events-calendar__panel discourse-events-calendar__external-calendars"
        aria-label="External calendars"
      >
        <div class="discourse-events-calendar__panel-header">
          <div>
            <h2>External calendars</h2>
            <p>Connect external accounts to sync calendars and RSVPs.</p>
          </div>
          <div class="discourse-events-calendar__panel-header-actions">
            {{#if @controller.canConnectEdgeosFromPanel}}
              <button
                type="button"
                class="btn btn-default"
                {{on "click" @controller.toggleEdgeosConnectForm}}
              >
                Connect EdgeOS
              </button>
            {{/if}}
            {{#if @controller.googleCalendarConfigured}}
              <a
                href="/calendar/external/connect/google"
                class="btn btn-primary"
                data-auto-route="false"
              >
                Connect Google
              </a>
            {{/if}}
            <button
              type="button"
              class="btn btn-flat discourse-events-calendar__panel-close"
              aria-label="Close external calendars"
              title="Close"
              {{on "click" @controller.hideExternalCalendarPanel}}
            >
              {{dIcon "xmark"}}
            </button>
          </div>
        </div>

        {{#if @controller.showEdgeosConnectForm}}
          <form
            class="discourse-events-calendar__edgeos-connect-form"
            {{on "submit" @controller.connectEdgeos}}
          >
            <label for="dec-edgeos-api-key">EdgeOS API key</label>
            <div class="discourse-events-calendar__edgeos-connect-row">
              <input
                id="dec-edgeos-api-key"
                type="password"
                autocomplete="off"
                value={{@controller.edgeosApiKeyInput}}
                placeholder="Paste your EdgeOS API key"
                {{on "input" @controller.setEdgeosApiKeyInput}}
              />
              <button
                type="submit"
                class="btn btn-primary"
                disabled={{@controller.isConnectingEdgeos}}
              >
                {{#if @controller.isConnectingEdgeos}}Connecting...{{else}}Connect{{/if}}
              </button>
            </div>
            <small>
              Create a key in the EdgeOS portal under API keys with the
              <code>events:read</code> scope. Your RSVPs appear under "My RSVPs"
              and sync hourly.
            </small>
          </form>
        {{/if}}

        {{#if @controller.externalCalendarError}}
          <div class="discourse-events-calendar__error" role="alert">
            {{@controller.externalCalendarError}}
          </div>
        {{/if}}

        {{#if @controller.externalCalendarNotice}}
          <div class="discourse-events-calendar__notice" role="status">
            {{@controller.externalCalendarNotice}}
          </div>
        {{/if}}

        {{#if @controller.isLoadingExternalCalendars}}
          <p class="discourse-events-calendar__empty">Loading external calendars...</p>
        {{else}}
          {{#if @controller.hasExternalCalendarConnections}}
            <div class="discourse-events-calendar__external-calendar-list">
              {{#each @controller.externalCalendarConnections as |connection|}}
                <article class="discourse-events-calendar__external-calendar">
                  <div>
                    <strong>{{connection.provider_label}}</strong>
                    {{#unless connection.is_edgeos}}
                      <span>{{connection.provider_user_id}}</span>
                    {{/unless}}
                    <span>{{connection.sync_status_label}} - {{connection.sync_label}}</span>
                    {{#if connection.is_edgeos}}
                      {{#if connection.sync_error}}
                        <span class="discourse-events-calendar__external-calendar-error">
                          Last sync failed: {{connection.sync_error}}
                        </span>
                      {{else if connection.sync_summary_label}}
                        <span>{{connection.sync_summary_label}}</span>
                      {{/if}}
                      <span>
                        Your EdgeOS RSVPs sync into "My RSVPs". One-way; RSVP
                        changes are made in EdgeOS.
                      </span>
                    {{/if}}
                  </div>

                  {{#if connection.is_google}}
                  <div class="discourse-events-calendar__external-calendar-settings">
                    <label>
                      <span>Categories</span>
                      <select disabled>
                        <option>All event categories</option>
                      </select>
                    </label>
                    <label>
                      <span>Direction</span>
                      <select disabled>
                        <option>One-way + RSVP replies</option>
                        <option>Two-way sync unavailable</option>
                      </select>
                    </label>
                    <label>
                      <span>Notifications</span>
                      <select disabled>
                        <option>Use Discourse notifications</option>
                      </select>
                    </label>
                  </div>
                  {{/if}}

                  <div class="discourse-events-calendar__external-calendar-actions">
                    <button
                      type="button"
                      class="btn btn-default"
                      disabled={{connection.is_syncing}}
                      {{on "click" (fn @controller.syncExternalConnection connection)}}
                    >
                      {{#if connection.is_syncing}}
                        Syncing...
                      {{else}}
                        Sync now
                      {{/if}}
                    </button>
                    <button
                      type="button"
                      class="btn btn-danger"
                      disabled={{connection.is_deleting}}
                      {{on "click" (fn @controller.disconnectExternalConnection connection)}}
                    >
                      {{#if connection.is_deleting}}
                        Disconnecting...
                      {{else}}
                        Disconnect
                      {{/if}}
                    </button>
                  </div>
                </article>
              {{/each}}
            </div>
          {{else}}
            <p class="discourse-events-calendar__empty">No external calendars connected.</p>
          {{/if}}
        {{/if}}
      </section>
    {{/if}}

    <nav class="discourse-events-calendar__section-tabs" aria-label="Calendar sections">
      <a
        href="/calendar"
        class="discourse-events-calendar__section-tab discourse-events-calendar__section-tab--active"
        aria-current="page"
      >
        Calendar
      </a>
      {{#if @controller.siteSettings.discourse_events_calendar_rooms_enabled}}
        <a href="/calendar/rooms" class="discourse-events-calendar__section-tab">
          Rooms
        </a>
      {{/if}}
      {{#if @controller.siteSettings.discourse_events_calendar_locations_enabled}}
        <a href="/calendar/locations" class="discourse-events-calendar__section-tab">
          Venues
        </a>
      {{/if}}
    </nav>

    <section class="discourse-events-calendar__panel discourse-events-calendar__browser">
      {{#unless @controller.showCustomBrowserTools}}
        <div class="discourse-events-calendar__panel-header">
          <div>
            <h2>{{@controller.browserPeriodLabel}}</h2>
            {{#if @controller.hasSelectedBrowserDate}}
              <p>{{@controller.selectedBrowserDateLabel}}</p>
            {{/if}}
          </div>
        </div>
      {{/unless}}

      <div class="discourse-events-calendar__mobile-filter-toggle">
        <button
          type="button"
          class="btn btn-default btn-small discourse-events-calendar__mobile-filter-button"
          aria-expanded={{@controller.isMobileFiltersExpanded}}
          {{on "click" @controller.toggleMobileFilters}}
        >
          {{dIcon "filter"}}
          <span>{{@controller.mobileFiltersToggleLabel}}</span>
        </button>

        {{#if @controller.showCollapsedMobileFilterClear}}
          <button
            type="button"
            class="btn btn-small discourse-events-calendar__mobile-filter-clear"
            {{on "click" @controller.clearBrowserFilters}}
          >
            Clear
          </button>
        {{/if}}
      </div>

      <div class={{@controller.browserFiltersClass}}>
        <div class="discourse-events-calendar__field discourse-events-calendar__time-filter-field">
          <label>When</label>
          <select
            aria-label="Event date range filter"
            value={{@controller.filter}}
            {{on "change" @controller.updateTimeFilter}}
          >
            <option value="upcoming" selected={{@controller.isUpcomingFilter}}>Upcoming</option>
            <option value="past" selected={{@controller.isPastFilter}}>Past</option>
            <option value="all" selected={{@controller.isAllFilter}}>All events</option>
          </select>
        </div>

        <div class="discourse-events-calendar__field discourse-events-calendar__search-filter-field">
          <label for="event-search">Search events</label>
          <Input
            id="event-search"
            @value={{@controller.searchQuery}}
            placeholder="Title, description, or location"
            {{on "input" @controller.updateSearchFilter}}
          />
        </div>

        <div class="discourse-events-calendar__field discourse-events-calendar__category-filter-field">
          <label for="event-category-filter">Category</label>
          <select
            id="event-category-filter"
            value={{@controller.category_id}}
            {{on "change" @controller.updateCategoryFilter}}
          >
            <option value="" selected={{@controller.hasNoCategoryFilter}}>All categories</option>
            {{#each @controller.categoryFilterOptions as |category|}}
              <option value={{category.id}} selected={{category.isSelected}}>{{category.name}}</option>
            {{/each}}
          </select>
        </div>

        <div class="discourse-events-calendar__field discourse-events-calendar__tag-filter-field">
          <label id="event-tag-filter-label">Tags</label>

          {{#if @controller.hasTags}}
            <div class="discourse-events-calendar__tag-filter-dropdown" {{dismissOnOutside @controller.closeTagFilterMenu}}>
              <button
                type="button"
                class="discourse-events-calendar__tag-filter-trigger"
                aria-labelledby="event-tag-filter-label"
                aria-haspopup="true"
                aria-expanded={{@controller.isTagFilterMenuOpen}}
                {{on "click" @controller.toggleTagFilterMenu}}
              >
                <span>{{@controller.selectedTagFilterLabel}}</span>
              </button>

              {{#if @controller.isTagFilterMenuOpen}}
              <div class="discourse-events-calendar__tag-filter-menu" role="group" aria-label="Filter by tags">
                <div class="discourse-events-calendar__tag-filter-menu-header">
                  <span>{{#if @controller.hasNoTagFilter}}Any tag{{else}}Any selected tag{{/if}}</span>
                  {{#unless @controller.hasNoTagFilter}}
                    <button
                      type="button"
                      class="btn-flat discourse-events-calendar__tag-filter-clear"
                      {{on "click" @controller.clearTagFilter}}
                    >
                      Clear
                    </button>
                  {{/unless}}
                </div>

                <div class="discourse-events-calendar__tag-filter-options">
                  {{#each @controller.tagFilterOptions as |tag|}}
                    <label class="discourse-events-calendar__tag-filter-option">
                      <input
                        type="checkbox"
                        checked={{tag.isSelected}}
                        {{on "change" (fn @controller.toggleTagFilter tag.id)}}
                      />
                      <span class="discourse-events-calendar__tag-filter-name">{{tag.name}}</span>
                    </label>
                  {{/each}}
                </div>
              </div>
              {{/if}}
            </div>
          {{else}}
            <p class="discourse-events-calendar__field-hint">No tags available</p>
          {{/if}}
        </div>

        {{#if @controller.canUsePersonalFilters}}
          <div class="discourse-events-calendar__personal-filters" role="group" aria-label="Personal event filters">
            <button
              type="button"
              class={{@controller.myRsvpsFilterButtonClass}}
              aria-pressed={{@controller.myRsvpsAriaPressed}}
              {{on "click" @controller.toggleMyRsvpsScope}}
            >
              {{dIcon "calendar-check"}}
              <span>My RSVPs</span>
            </button>
            <button
              type="button"
              class={{@controller.myEventsFilterButtonClass}}
              aria-pressed={{@controller.myEventsAriaPressed}}
              {{on "click" @controller.toggleOrganizingScope}}
            >
              {{dIcon "crown"}}
              <span>My Events</span>
            </button>
          </div>

          {{#if @controller.shouldShowEdgeosRsvpHint}}
            <div class="discourse-events-calendar__edgeos-rsvp-hint" role="note">
              <span>
                Have RSVPs in EdgeOS? Connect your EdgeOS account to see them here.
              </span>
              <a href="/my/preferences/external-calendars" data-auto-route="false">Connect EdgeOS</a>
              <button
                type="button"
                class="btn btn-flat btn-small"
                aria-label="Dismiss"
                {{on "click" @controller.dismissEdgeosRsvpHint}}
              >
                {{dIcon "xmark"}}
              </button>
            </div>
          {{/if}}

        {{/if}}

          <div class="discourse-events-calendar__advanced-filters">
            {{#if @controller.canUsePersonalFilters}}
              <div class="discourse-events-calendar__field discourse-events-calendar__rsvp-status-filter-field">
                <label for="event-rsvp-status-filter">My response</label>
                <select
                  id="event-rsvp-status-filter"
                  value={{@controller.rsvp_status}}
                  {{on "change" @controller.updateRsvpStatusFilter}}
                >
                  {{#each @controller.rsvpStatusOptions as |status|}}
                    <option value={{status.value}} selected={{status.isSelected}}>{{status.label}}</option>
                  {{/each}}
                </select>
              </div>
            {{/if}}

            {{#if @controller.hasLocations}}
              <div class="discourse-events-calendar__field">
                <label for="event-location-filter">Location</label>
                <select
                  id="event-location-filter"
                  value={{@controller.location_id}}
                  {{on "change" @controller.updateLocationFilter}}
                >
                  <option value="" selected={{@controller.hasNoLocationFilter}}>All locations</option>
                  {{#each @controller.locationFilterOptions as |location|}}
                    <option value={{location.value}} selected={{location.isSelected}}>{{location.name}}</option>
                  {{/each}}
                </select>
              </div>
            {{/if}}

            <div class="discourse-events-calendar__field">
              <label for="event-room-filter">Room</label>
              <select
                id="event-room-filter"
                value={{@controller.room_id}}
                {{on "change" @controller.updateRoomFilter}}
              >
                <option value="" selected={{@controller.hasNoRoomFilter}}>All rooms</option>
                {{#each @controller.roomFilterOptions as |room|}}
                  <option value={{room.id}} selected={{room.isSelected}}>{{room.name}}</option>
                {{/each}}
              </select>
            </div>

            {{#if @controller.hasOrganizers}}
              <div class="discourse-events-calendar__field">
                <label for="event-organizer-filter">Organizer</label>
                <select
                  id="event-organizer-filter"
                  value={{@controller.owner_id}}
                  {{on "change" @controller.updateOrganizerFilter}}
                >
                  <option value="" selected={{@controller.hasNoOwnerFilter}}>All organizers</option>
                  {{#each @controller.organizerFilterOptions as |organizer|}}
                    <option value={{organizer.id}} selected={{organizer.isSelected}}>{{organizer.label}}</option>
                  {{/each}}
                </select>
              </div>
            {{/if}}

            {{#if @controller.canUsePersonalFilters}}
              <div class="discourse-events-calendar__field">
                <label for="event-visibility-filter">Visibility</label>
                <select
                  id="event-visibility-filter"
                  value={{@controller.event_visibility}}
                  {{on "change" @controller.updateEventVisibilityFilter}}
                >
                  {{#each @controller.eventVisibilityOptions as |visibility|}}
                    <option value={{visibility.value}} selected={{visibility.isSelected}}>{{visibility.label}}</option>
                  {{/each}}
                </select>
              </div>
            {{/if}}

            {{#if @controller.hasActiveBrowserFilters}}
              <button
                type="button"
                class="btn btn-small discourse-events-calendar__filter-action-clear"
                {{on "click" @controller.clearBrowserFilters}}
              >
                Clear
              </button>
            {{/if}}
          </div>
      </div>

      {{#if @controller.showCustomBrowserTools}}
        <div class="discourse-events-calendar__calendar-toolbar discourse-events-calendar__calendar-toolbar--desktop">
          <div class="discourse-events-calendar__month-controls">
            <button
              type="button"
              class="btn btn-small"
              aria-label={{@controller.previousPeriodLabel}}
              {{on "click" @controller.previousBrowserMonth}}
            >
              &lt;
            </button>
            <button type="button" class="btn btn-small" {{on "click" @controller.todayBrowserMonth}}>
              Today
            </button>
            <button
              type="button"
              class="btn btn-small"
              aria-label={{@controller.nextPeriodLabel}}
              {{on "click" @controller.nextBrowserMonth}}
            >
              &gt;
            </button>
          </div>

          <div class="discourse-events-calendar__calendar-period" aria-live="polite">
            <h2>{{@controller.browserPeriodLabel}}</h2>
            {{#if @controller.hasSelectedBrowserDate}}
              <p>{{@controller.selectedBrowserDateLabel}}</p>
            {{/if}}
          </div>

          <div class="discourse-events-calendar__view-toggle" role="tablist" aria-label="Calendar view">
            <button
              type="button"
              class={{@controller.monthViewTabClass}}
              role="tab"
              aria-selected={{@controller.isMonthView}}
              {{on "click" @controller.showMonthView}}
            >
              Month
            </button>
            <button
              type="button"
              class={{@controller.weekViewTabClass}}
              role="tab"
              aria-selected={{@controller.isWeekView}}
              {{on "click" @controller.showWeekView}}
            >
              Week
            </button>
            <button
              type="button"
              class={{@controller.dayViewTabClass}}
              role="tab"
              aria-selected={{@controller.isDayView}}
              {{on "click" @controller.showDayView}}
            >
              Day
            </button>
            <button
              type="button"
              class={{@controller.venueViewTabClass}}
              role="tab"
              aria-selected={{@controller.isVenueView}}
              {{on "click" @controller.showVenueView}}
            >
              By venue
            </button>
            <button
              type="button"
              class={{@controller.agendaViewTabClass}}
              role="tab"
              aria-selected={{@controller.isAgendaView}}
              {{on "click" @controller.showAgendaView}}
            >
              List
            </button>
          </div>
        </div>

        <div class="discourse-events-calendar__calendar-toolbar discourse-events-calendar__calendar-toolbar--mobile">
          <div class="discourse-events-calendar__calendar-period" aria-live="polite">
            <h2>{{@controller.browserPeriodLabel}}</h2>
            {{#if @controller.hasSelectedBrowserDate}}
              <p>{{@controller.selectedBrowserDateLabel}}</p>
            {{/if}}
          </div>

          <div class="discourse-events-calendar__month-controls">
            <button
              type="button"
              class="btn btn-small"
              aria-label={{@controller.previousPeriodLabel}}
              {{on "click" @controller.previousBrowserMonth}}
            >
              &lt;
            </button>
            <button type="button" class="btn btn-small" {{on "click" @controller.todayBrowserMonth}}>
              Today
            </button>
            <button
              type="button"
              class="btn btn-small"
              aria-label={{@controller.nextPeriodLabel}}
              {{on "click" @controller.nextBrowserMonth}}
            >
              &gt;
            </button>
          </div>

          <div class="discourse-events-calendar__view-toggle" role="tablist" aria-label="Calendar view">
            <button
              type="button"
              class={{@controller.monthViewTabClass}}
              role="tab"
              aria-selected={{@controller.isMonthView}}
              {{on "click" @controller.showMonthView}}
            >
              Month
            </button>
            <button
              type="button"
              class={{@controller.weekViewTabClass}}
              role="tab"
              aria-selected={{@controller.isWeekView}}
              {{on "click" @controller.showWeekView}}
            >
              Week
            </button>
            <button
              type="button"
              class={{@controller.dayViewTabClass}}
              role="tab"
              aria-selected={{@controller.isDayView}}
              {{on "click" @controller.showDayView}}
            >
              Day
            </button>
            <button
              type="button"
              class={{@controller.venueViewTabClass}}
              role="tab"
              aria-selected={{@controller.isVenueView}}
              {{on "click" @controller.showVenueView}}
            >
              By venue
            </button>
            <button
              type="button"
              class={{@controller.agendaViewTabClass}}
              role="tab"
              aria-selected={{@controller.isAgendaView}}
              {{on "click" @controller.showAgendaView}}
            >
              List
            </button>
          </div>
        </div>
      {{/if}}

      {{#if @controller.eventsLoadError}}
        <div class="discourse-events-calendar__events-load-error" role="alert">
          <span>{{@controller.eventsLoadError}}</span>
          <button
            type="button"
            class="btn btn-small btn-default"
            disabled={{@controller.isLoadingEvents}}
            {{on "click" @controller.retryEventsLoad}}
          >
            {{i18n "discourse_events_calendar.event_loading.try_again"}}
          </button>
        </div>
      {{/if}}

      {{#if @controller.eventsLoadStatusMessage}}
        <div
          class={{@controller.eventsLoadStatusClass}}
          role={{@controller.eventsLoadStatusRole}}
          aria-live="polite"
        >
          <span>{{@controller.eventsLoadStatusMessage}}</span>
          <div class="discourse-events-calendar__events-load-actions">
            {{#if @controller.eventsLoadCanRetry}}
              <button
                type="button"
                class="btn btn-small btn-default"
                disabled={{@controller.isLoadingEvents}}
                {{on "click" @controller.retryEventBatches}}
              >
                {{i18n "discourse_events_calendar.event_loading.retry_remaining"}}
              </button>
            {{/if}}
            {{#if @controller.canLoadMoreEventBatches}}
              <button
                type="button"
                class="btn btn-small btn-primary"
                {{on "click" @controller.loadMoreEventBatches}}
              >
                {{i18n "discourse_events_calendar.event_loading.load_more"}}
              </button>
            {{/if}}
            {{#if @controller.canLoadAllEventBatches}}
              <button
                type="button"
                class="btn btn-small btn-default"
                {{on "click" @controller.loadAllEventBatches}}
              >
                {{i18n "discourse_events_calendar.event_loading.load_all"}}
              </button>
            {{/if}}
            {{#if @controller.eventsLoadIsPartial}}
              <button
                type="button"
                class="btn btn-small btn-default"
                {{on "click" @controller.refineEventResults}}
              >
                {{i18n "discourse_events_calendar.event_loading.refine_results"}}
              </button>
            {{/if}}
          </div>
        </div>
      {{/if}}

      {{#if @controller.showGenericEventsLoading}}
        <div class="discourse-events-calendar__events-loading" role="status">
          {{i18n "discourse_events_calendar.event_loading.loading"}}
        </div>
      {{/if}}

      {{#if @controller.isMonthView}}
          <div class="discourse-events-calendar__month-calendar" aria-label="Month calendar">
          <div class="discourse-events-calendar__month-weekdays">
            <span>Sun</span>
            <span>Mon</span>
            <span>Tue</span>
            <span>Wed</span>
            <span>Thu</span>
            <span>Fri</span>
            <span>Sat</span>
          </div>

          <div class="discourse-events-calendar__month-days">
            {{#each @controller.browserMonthDays as |day|}}
              {{#if day.isSpacer}}
                <span class="discourse-events-calendar__month-spacer"></span>
              {{else}}
                <div
                  class={{day.className}}
                  data-date={{day.date}}
                  {{on "click" (fn @controller.openDayFromMonthCell day.date)}}
                  {{on "mouseleave" (fn @controller.maybeDismissMonthDayPreview day.date)}}
                >
                  <div class="discourse-events-calendar__month-date-row">
                    <button
                      type="button"
                      class="discourse-events-calendar__month-date"
                      aria-pressed={{day.isSelected}}
                      aria-label="Open day view for {{day.date}}"
                      {{on "click" (fn @controller.selectBrowserDate day.date)}}
                    >
                      {{day.day}}
                    </button>

                    {{#if @controller.canCreateEvents}}
                      <button
                        type="button"
                        class="discourse-events-calendar__date-add"
                        aria-label="Add event on {{day.date}}"
                        {{on "click" (fn @controller.addEventOnDate day.date)}}
                      >
                        +
                      </button>
                    {{/if}}
                  </div>
                  {{#each day.visibleEvents as |event|}}
                    <a
                      href={{event.detailUrl}}
                      aria-label={{event.hoverTitle}}
                      class="discourse-events-calendar__month-event discourse-events-calendar__event-preview-trigger {{event.stateClass}}"
                      {{on "mousedown" @controller.cancelPendingEventPreview}}
                      {{on "touchstart" (fn @controller.startEventTouchPreview event)}}
                      {{on "touchmove" (fn @controller.moveEventTouchPreview event)}}
                      {{on "touchend" (fn @controller.finishEventTouchPreview event)}}
                      {{on "touchcancel" @controller.cancelEventTouchPreview}}
                      {{on "contextmenu" @controller.preventEventContextMenu}}
                      {{on "mouseenter" (fn @controller.showEventPreview event)}}
                      {{on "focus" (fn @controller.showEventPreview event)}}
                    >
                      {{#if event.monthTime}}
                        <span class="discourse-events-calendar__month-event-time">{{event.monthTime}}</span>
                      {{/if}}
                      <span class="discourse-events-calendar__month-event-title">{{event.title}}</span>
                    </a>
                  {{/each}}
                  {{#if day.moreCount}}
                    <button
                      type="button"
                      class="discourse-events-calendar__month-more"
                      aria-expanded={{day.isMoreOpen}}
                      aria-label="Show all events for {{day.date}}"
                      {{on "click" (fn @controller.openMonthMore day.date)}}
                    >
                      +{{day.moreCount}} more
                    </button>
                  {{/if}}
                  {{#if day.isMoreOpen}}
                    <section
                      class={{day.morePopoverClass}}
                      aria-label="Events for {{day.date}}"
                      aria-busy={{day.isMoreLoading}}
                      {{on "mousedown" @controller.keepMonthMoreOpen}}
                      {{on "click" @controller.keepMonthMoreOpen}}
                    >
                      <header class="discourse-events-calendar__month-more-popover-header">
                        <strong>{{day.date}}</strong>
                        <button
                          type="button"
                          aria-label="Close event list"
                          {{on "click" @controller.closeMonthMore}}
                        >
                          x
                        </button>
                      </header>
                      <ol class="discourse-events-calendar__month-more-popover-events">
                        {{#if day.isMoreLoading}}
                          <li
                            class="discourse-events-calendar__month-more-loading"
                            aria-live="polite"
                            role="status"
                          >
                            <span
                              class="discourse-events-calendar__month-more-loading-spinner"
                              aria-hidden="true"
                            ></span>
                            <span>Loading events...</span>
                          </li>
                        {{/if}}
                        {{#each day.moreEvents as |event|}}
                          <li>
                            <a
                              href={{event.detailUrl}}
                              class="discourse-events-calendar__month-more-event discourse-events-calendar__event-preview-trigger {{event.stateClass}}"
                              aria-label={{event.hoverTitle}}
                              {{on "mousedown" @controller.cancelPendingEventPreview}}
                              {{on "click" (fn @controller.openEventPreview event)}}
                              {{on "touchstart" (fn @controller.startEventTouchPreview event)}}
                              {{on "touchmove" (fn @controller.moveEventTouchPreview event)}}
                              {{on "touchend" (fn @controller.finishEventTouchPreview event)}}
                              {{on "touchcancel" @controller.cancelEventTouchPreview}}
                              {{on "contextmenu" @controller.preventEventContextMenu}}
                            >
                              {{#if event.monthTime}}
                                <span>{{event.monthTime}}</span>
                              {{/if}}
                              <strong>{{event.title}}</strong>
                            </a>
                          </li>
                        {{/each}}
                      </ol>
                    </section>
                  {{/if}}
                </div>
              {{/if}}
            {{/each}}
          </div>
          </div>
        {{else}}
          {{#if @controller.isWeekView}}
          <div class={{@controller.timeGridScrollShellClass}}>
            <span
              aria-hidden="true"
              class="discourse-events-calendar__time-grid-scroll-cue discourse-events-calendar__time-grid-scroll-cue--left"
            ></span>
            <span
              aria-hidden="true"
              class="discourse-events-calendar__time-grid-scroll-cue discourse-events-calendar__time-grid-scroll-cue--right"
            ></span>
            <div
              class="discourse-events-calendar__time-grid-scroll"
              tabindex="0"
              {{didInsert @controller.updateTimeGridScrollCue}}
              {{on "scroll" @controller.updateTimeGridScrollCue}}
              {{on "wheel" @controller.handleTimeGridWheel}}
            >
              <div class="discourse-events-calendar__time-grid discourse-events-calendar__time-grid--week">
                <div class="discourse-events-calendar__time-grid-header">
                  <span class="discourse-events-calendar__time-grid-corner"></span>
                  {{#each @controller.weekTimeGridDays as |day|}}
                    <div class="discourse-events-calendar__time-grid-day-heading">
                      <button
                        type="button"
                        class="discourse-events-calendar__time-grid-day-link"
                        aria-label="Open day view for {{day.date}}"
                        {{on "click" (fn @controller.selectBrowserDate day.date)}}
                      >
                        <span>{{day.dayName}}</span>
                        <strong>{{day.dayNumber}}</strong>
                      </button>

                      {{#if @controller.canCreateEvents}}
                        <button
                          type="button"
                          class="discourse-events-calendar__date-add discourse-events-calendar__time-grid-date-add"
                          aria-label="Add event on {{day.date}}"
                          {{on "click" (fn @controller.addEventOnDate day.date)}}
                        >
                          +
                        </button>
                      {{/if}}
                    </div>
                  {{/each}}
                </div>

                <div class="discourse-events-calendar__time-grid-all-day-row">
                  <span class="discourse-events-calendar__time-grid-all-day-label">All day</span>
                  <div class="discourse-events-calendar__time-grid-all-day-days">
                    {{#each @controller.weekTimeGridDays as |day|}}
                      <section class="discourse-events-calendar__time-grid-all-day-cell" aria-label="All-day events for {{day.label}}">
                        {{#each day.allDayEvents as |event|}}
                          <a
                            href={{event.detailUrl}}
                            aria-label={{event.hoverTitle}}
                            class="discourse-events-calendar__time-grid-all-day-event discourse-events-calendar__event-preview-trigger {{event.stateClass}}"
                            {{on "click" (fn @controller.openEventDetail event)}}
                            {{on "touchstart" (fn @controller.startEventTouchPreview event)}}
                            {{on "touchmove" (fn @controller.moveEventTouchPreview event)}}
                            {{on "touchend" (fn @controller.finishEventTouchPreview event)}}
                            {{on "touchcancel" @controller.cancelEventTouchPreview}}
                            {{on "contextmenu" @controller.preventEventContextMenu}}
                            {{on "mouseenter" (fn @controller.showEventPreview event)}}
                            {{on "focus" (fn @controller.showEventPreview event)}}
                            {{on "mouseleave" (fn @controller.maybeDismissTimeGridPreview event)}}
                          >
                            {{event.title}}
                          </a>
                        {{/each}}
                      </section>
                    {{/each}}
                  </div>
                </div>

                <div
                  class="discourse-events-calendar__time-grid-body"
                  tabindex="0"
                  aria-label="Scrollable 24-hour week schedule"
                  {{didInsert @controller.focusTimeGridWorkday}}
                  {{didUpdate @controller.focusTimeGridWorkday @controller.timeGridScrollFocusSignature}}
                  {{on "wheel" @controller.handleTimeGridWheel}}
                >
                  <div class="discourse-events-calendar__time-grid-axis" aria-hidden="true">
                    {{#each @controller.timeGridHours as |hour|}}
                      <span>{{hour.label}}</span>
                    {{/each}}
                  </div>

                  <div class="discourse-events-calendar__time-grid-days">
                    {{#each @controller.weekTimeGridDays as |day|}}
                      <section
                        class={{day.className}}
                        aria-label={{day.label}}
                        {{on "pointerdown" (fn @controller.startTimeGridDraft day.date)}}
                        {{on "pointermove" (fn @controller.moveTimeGridDraft day.date)}}
                        {{on "pointerup" (fn @controller.finishTimeGridDraft day.date)}}
                        {{on "pointercancel" (fn @controller.cancelTimeGridDraft day.date)}}
                      >
                        {{#each day.events as |event|}}
                          <a
                            href={{event.detailUrl}}
                            aria-label={{event.hoverTitle}}
                            style={{event.timeGridStyle}}
                            class="discourse-events-calendar__time-grid-event discourse-events-calendar__event-preview-trigger {{event.stateClass}}"
                            {{on "click" (fn @controller.openEventDetail event)}}
                            {{on "touchstart" (fn @controller.startEventTouchPreview event)}}
                            {{on "touchmove" (fn @controller.moveEventTouchPreview event)}}
                            {{on "touchend" (fn @controller.finishEventTouchPreview event)}}
                            {{on "touchcancel" @controller.cancelEventTouchPreview}}
                            {{on "contextmenu" @controller.preventEventContextMenu}}
                            {{on "mouseenter" (fn @controller.showEventPreview event)}}
                            {{on "focus" (fn @controller.showEventPreview event)}}
                            {{on "mouseleave" (fn @controller.maybeDismissTimeGridPreview event)}}
                          >
                            <span class="discourse-events-calendar__time-grid-event-title">{{event.title}}</span>
                            {{#if event.displayTimeRange}}
                              <span class="discourse-events-calendar__time-grid-event-time">
                                {{event.displayTimeRange}}
                              </span>
                            {{/if}}
                            {{#if event.location}}
                              <span class="discourse-events-calendar__time-grid-event-meta">
                                {{event.location}}
                              </span>
                            {{/if}}
                          </a>
                        {{/each}}
                        {{#if day.createSelection}}
                          <div
                            class={{day.createSelection.className}}
                            style={{day.createSelection.style}}
                          >
                            <span>New event</span>
                            <strong>{{day.createSelection.label}}</strong>
                            {{#if day.createSelection.isCommitted}}
                              <div class="discourse-events-calendar__time-grid-create-actions">
                                <button
                                  type="button"
                                  class="btn btn-small btn-primary"
                                  {{on "click" (fn @controller.confirmTimeGridDraft day.date)}}
                                >
                                  Create event
                                </button>
                                <button
                                  type="button"
                                  class="btn btn-small discourse-events-calendar__time-grid-draft-cancel"
                                  {{on "click" @controller.clearTimeGridDraft}}
                                >
                                  Cancel
                                </button>
                              </div>
                            {{/if}}
                          </div>
                        {{/if}}
                      </section>
                    {{/each}}
                  </div>
                </div>
              </div>
            </div>
          </div>
        {{else}}
          {{#if @controller.isDayView}}
            <div class="discourse-events-calendar__day-date-strip" aria-label="Day schedule dates">
              {{#each @controller.dayDateStripDays as |day|}}
                <div class={{day.className}}>
                  <button
                    type="button"
                    class="discourse-events-calendar__day-date-button"
                    aria-pressed={{day.isSelected}}
                    aria-label="Open day view for {{day.label}}"
                    {{on "click" (fn @controller.selectDayDate day.date)}}
                  >
                    <span>{{day.dayName}}</span>
                    <strong>{{day.dayNumber}}</strong>
                  </button>

                  {{#if @controller.canCreateEvents}}
                    <button
                      type="button"
                      class="discourse-events-calendar__date-add discourse-events-calendar__day-date-add"
                      aria-label="Add event on {{day.label}}"
                      {{on "click" (fn @controller.addEventOnDate day.date)}}
                    >
                      +
                    </button>
                  {{/if}}
                </div>
              {{/each}}
            </div>

            <div class="discourse-events-calendar__time-grid discourse-events-calendar__time-grid--day">
              <div class="discourse-events-calendar__time-grid-all-day-row">
                <span class="discourse-events-calendar__time-grid-all-day-label">All day</span>
                <div class="discourse-events-calendar__time-grid-all-day-days">
                  {{#each @controller.dayTimeGridDays as |day|}}
                    <section class="discourse-events-calendar__time-grid-all-day-cell" aria-label="All-day events for {{day.label}}">
                      {{#each day.allDayEvents as |event|}}
                        <a
                          href={{event.detailUrl}}
                          aria-label={{event.hoverTitle}}
                          class="discourse-events-calendar__time-grid-all-day-event discourse-events-calendar__event-preview-trigger {{event.stateClass}}"
                          {{on "click" (fn @controller.openEventDetail event)}}
                          {{on "touchstart" (fn @controller.startEventTouchPreview event)}}
                          {{on "touchmove" (fn @controller.moveEventTouchPreview event)}}
                          {{on "touchend" (fn @controller.finishEventTouchPreview event)}}
                          {{on "touchcancel" @controller.cancelEventTouchPreview}}
                          {{on "contextmenu" @controller.preventEventContextMenu}}
                          {{on "mouseenter" (fn @controller.showEventPreview event)}}
                          {{on "focus" (fn @controller.showEventPreview event)}}
                          {{on "mouseleave" (fn @controller.maybeDismissTimeGridPreview event)}}
                        >
                          {{event.title}}
                        </a>
                      {{/each}}
                    </section>
                  {{/each}}
                </div>
              </div>

              <div
                class="discourse-events-calendar__time-grid-body"
                tabindex="0"
                aria-label="Scrollable 24-hour day schedule"
                {{didInsert @controller.focusTimeGridWorkday}}
                {{didUpdate @controller.focusTimeGridWorkday @controller.timeGridScrollFocusSignature}}
                {{on "wheel" @controller.handleTimeGridWheel}}
              >
                <div class="discourse-events-calendar__time-grid-axis" aria-hidden="true">
                  {{#each @controller.timeGridHours as |hour|}}
                    <span>{{hour.label}}</span>
                  {{/each}}
                </div>

                <div class="discourse-events-calendar__time-grid-days">
                  {{#each @controller.dayTimeGridDays as |day|}}
                    <section
                      class={{day.className}}
                      aria-label={{day.label}}
                      {{on "pointerdown" (fn @controller.startTimeGridDraft day.date)}}
                      {{on "pointermove" (fn @controller.moveTimeGridDraft day.date)}}
                      {{on "pointerup" (fn @controller.finishTimeGridDraft day.date)}}
                      {{on "pointercancel" (fn @controller.cancelTimeGridDraft day.date)}}
                    >
                      {{#each day.events as |event|}}
                        <a
                          href={{event.detailUrl}}
                          aria-label={{event.hoverTitle}}
                          style={{event.timeGridStyle}}
                          class="discourse-events-calendar__time-grid-event discourse-events-calendar__event-preview-trigger {{event.stateClass}}"
                          {{on "click" (fn @controller.openEventDetail event)}}
                          {{on "touchstart" (fn @controller.startEventTouchPreview event)}}
                          {{on "touchmove" (fn @controller.moveEventTouchPreview event)}}
                          {{on "touchend" (fn @controller.finishEventTouchPreview event)}}
                          {{on "touchcancel" @controller.cancelEventTouchPreview}}
                          {{on "contextmenu" @controller.preventEventContextMenu}}
                          {{on "mouseenter" (fn @controller.showEventPreview event)}}
                          {{on "focus" (fn @controller.showEventPreview event)}}
                          {{on "mouseleave" (fn @controller.maybeDismissTimeGridPreview event)}}
                        >
                          <span class="discourse-events-calendar__time-grid-event-title">{{event.title}}</span>
                          {{#if event.displayTimeRange}}
                            <span class="discourse-events-calendar__time-grid-event-time">
                              {{event.displayTimeRange}}
                            </span>
                          {{/if}}
                          {{#if event.location}}
                            <span class="discourse-events-calendar__time-grid-event-meta">
                              {{event.location}}
                            </span>
                          {{/if}}
                        </a>
                      {{/each}}
                      {{#if day.createSelection}}
                        <div
                          class={{day.createSelection.className}}
                          style={{day.createSelection.style}}
                        >
                          <span>New event</span>
                          <strong>{{day.createSelection.label}}</strong>
                          {{#if day.createSelection.isCommitted}}
                            <div class="discourse-events-calendar__time-grid-create-actions">
                              <button
                                type="button"
                                class="btn btn-small btn-primary"
                                {{on "click" (fn @controller.confirmTimeGridDraft day.date)}}
                              >
                                Create event
                              </button>
                              <button
                                type="button"
                                class="btn btn-small discourse-events-calendar__time-grid-draft-cancel"
                                {{on "click" @controller.clearTimeGridDraft}}
                              >
                                Cancel
                              </button>
                            </div>
                          {{/if}}
                        </div>
                      {{/if}}
                    </section>
                  {{/each}}
                </div>
              </div>
            </div>
          {{else}}
            {{#if @controller.isVenueView}}
              <div class="discourse-events-calendar__venue-view">
                <div class="discourse-events-calendar__venue-date-strip" aria-label="Venue schedule dates">
                  {{#each @controller.venueDateStripDays as |day|}}
                    <button
                      type="button"
                      class={{day.className}}
                      aria-pressed={{day.isSelected}}
                      {{on "click" (fn @controller.selectVenueDate day.date)}}
                    >
                      <span>{{day.dayName}}</span>
                      <strong>{{day.dayNumber}}</strong>
                    </button>
                  {{/each}}
                </div>

                <div class="discourse-events-calendar__venue-columns">
                  {{#each @controller.venueColumns as |column|}}
                    <section class="discourse-events-calendar__venue-column" aria-label={{column.title}}>
                      <header class={{column.headerClass}}>
                        {{#if column.imageUrl}}
                          <img
                            src={{column.imageUrl}}
                            alt=""
                            loading="lazy"
                            class="discourse-events-calendar__venue-column-image"
                          />
                        {{/if}}

                        <div class="discourse-events-calendar__venue-column-header-content">
                          <div class="discourse-events-calendar__venue-column-title-row">
                            {{#if column.detailUrl}}
                              <a href={{column.detailUrl}}>
                                {{column.title}}
                              </a>
                            {{else}}
                              <span>{{column.title}}</span>
                            {{/if}}

                            {{#if @controller.canCreateEvents}}
                              {{#if column.canQuickAdd}}
                                <button
                                  type="button"
                                  class="discourse-events-calendar__venue-add"
                                  aria-label="Add event for {{column.title}} on {{@controller.browserPeriodLabel}}"
                                  {{on "click" (fn @controller.addEventForVenueColumn column)}}
                                >
                                  +
                                </button>
                              {{/if}}
                            {{/if}}
                          </div>

                          {{#if column.location}}
                            <a href={{column.locationUrl}}>
                              {{column.location}}
                            </a>
                          {{/if}}
                        </div>
                      </header>

                      {{#if column.events.length}}
                        <ol class="discourse-events-calendar__venue-events">
                          {{#each column.events as |event|}}
                            <li>
                              <a
                                href={{event.detailUrl}}
                                aria-label={{event.hoverTitle}}
                                class="discourse-events-calendar__venue-event discourse-events-calendar__event-preview-trigger {{event.stateClass}}"
                                {{on "click" (fn @controller.openEventDetail event)}}
                                {{on "touchstart" (fn @controller.startEventTouchPreview event)}}
                                {{on "touchmove" (fn @controller.moveEventTouchPreview event)}}
                                {{on "touchend" (fn @controller.finishEventTouchPreview event)}}
                                {{on "touchcancel" @controller.cancelEventTouchPreview}}
                                {{on "contextmenu" @controller.preventEventContextMenu}}
                                {{on "mouseenter" (fn @controller.showEventPreview event)}}
                                {{on "focus" (fn @controller.showEventPreview event)}}
                                {{on "mouseleave" (fn @controller.maybeDismissEventPreview event)}}
                              >
                                <span class="discourse-events-calendar__venue-event-title">{{event.title}}</span>
                                {{#if event.displayTimeRange}}
                                  <span class="discourse-events-calendar__venue-event-time">
                                    {{event.displayTimeRange}}
                                  </span>
                                {{/if}}
                                {{#if event.rsvpSummary}}
                                  <span class="discourse-events-calendar__venue-event-meta">
                                    {{event.rsvpSummary}}
                                  </span>
                                {{/if}}
                              </a>
                            </li>
                          {{/each}}
                        </ol>
                      {{else}}
                        <p class="discourse-events-calendar__range-empty">No events</p>
                      {{/if}}
                    </section>
                  {{/each}}
                </div>
              </div>
            {{else}}
              <div class="discourse-events-calendar__schedule-list">
                {{#if @controller.agendaGroups.length}}
                  <ol class="discourse-events-calendar__schedule-groups">
                    {{#each @controller.agendaGroups as |group|}}
                      <li class="discourse-events-calendar__schedule-group">
                        <div class="discourse-events-calendar__schedule-date-divider">
                          <span
                            class="discourse-events-calendar__schedule-date-dot"
                            aria-hidden="true"
                          ></span>
                          <h4 class="discourse-events-calendar__schedule-date-label">
                            {{group.dayName}}, {{group.monthLabel}} {{group.dayNumber}}
                          </h4>
                          <span
                            class="discourse-events-calendar__schedule-date-rule"
                            aria-hidden="true"
                          ></span>
                        </div>

                        <ol class="discourse-events-calendar__schedule-events" aria-label={{group.fullLabel}}>
                          {{#each group.events as |event|}}
                            <li class="discourse-events-calendar__schedule-event-row">
                              <div class="discourse-events-calendar__schedule-event-shell {{event.stateClass}}">
                                <div class="discourse-events-calendar__schedule-aside">
                                  {{#if event.image_url}}
                                    <a
                                      href={{event.detailUrl}}
                                      class="discourse-events-calendar__schedule-thumb discourse-events-calendar__event-preview-trigger"
                                      aria-label={{event.title}}
                                      {{on "click" (fn @controller.openEventPreview event)}}
                                    >
                                      <img src={{event.image_url}} alt="" loading="lazy" />
                                    </a>
                                  {{/if}}

                                  <div class="discourse-events-calendar__schedule-actions" aria-label="Actions for {{event.title}}">
                                    {{#if event.canPreviewRsvp}}
                                      <button
                                        type="button"
                                        class="btn btn-primary btn-small discourse-events-calendar__schedule-rsvp"
                                        disabled={{@controller.isSavingPreviewRsvp}}
                                        {{on "click" (fn @controller.rsvpPreviewEvent event)}}
                                      >
                                        {{event.previewRsvpLabel}}
                                      </button>
                                    {{/if}}

                                    {{#if event.canPreviewExternalRsvp}}
                                      <a
                                        class="btn btn-primary btn-small discourse-events-calendar__schedule-rsvp"
                                        href={{event.external_rsvp_url}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        RSVP
                                      </a>
                                    {{/if}}

                                    <button
                                      type="button"
                                      class="btn btn-small discourse-events-calendar__schedule-hide"
                                      title="Hide"
                                      aria-label="Hide {{event.title}}"
                                      {{on "click" (fn @controller.hidePreviewEvent event)}}
                                    >
                                      {{dIcon "eye"}}
                                    </button>
                                  </div>
                                </div>

                                <a
                                  href={{event.detailUrl}}
                                  title={{event.hoverTitle}}
                                  aria-label={{event.hoverTitle}}
                                  class="discourse-events-calendar__schedule-event discourse-events-calendar__event-preview-trigger"
                                  {{on "click" (fn @controller.openEventPreview event)}}
                                >
                                  <span class="discourse-events-calendar__schedule-headline">
                                    <span class="discourse-events-calendar__schedule-time">
                                      {{event.displayTimeRange}}
                                    </span>
                                    <span class="discourse-events-calendar__schedule-status">
                                      {{event.statusLabel}}
                                    </span>
                                  </span>

                                  <strong class="discourse-events-calendar__schedule-title">
                                    {{event.title}}
                                  </strong>

                                  {{#if event.previewDescription}}
                                    <span class="discourse-events-calendar__schedule-description">
                                      {{event.previewDescription}}
                                    </span>
                                  {{/if}}

                                  {{#if event.scheduleMeta.length}}
                                    <span class="discourse-events-calendar__schedule-meta">
                                      {{#each event.scheduleMeta as |item|}}
                                        <em>{{item}}</em>
                                      {{/each}}
                                    </span>
                                  {{/if}}
                                </a>
                              </div>
                            </li>
                          {{/each}}
                        </ol>
                      </li>
                    {{/each}}
                  </ol>
                {{else}}
                  <p class="discourse-events-calendar__empty">
                    {{@controller.emptyText}}
                    {{#if @controller.shouldOfferAllEventsForMyRsvps}}
                      <button
                        type="button"
                        class="btn btn-small btn-default"
                        {{on "click" @controller.showAllEvents}}
                      >
                        Show all my RSVPs
                      </button>
                    {{/if}}
                  </p>
                {{/if}}
              </div>
            {{/if}}
          {{/if}}
        {{/if}}
      {{/if}}

      {{#if @controller.selectedPreviewEvent}}
        <DecEventPreviewPopover
          @className={{@controller.eventPreviewClass}}
          @event={{@controller.selectedPreviewEvent}}
          @isSavingRsvp={{@controller.isSavingSelectedPreviewRsvp}}
          @onClose={{@controller.closeEventPreview}}
          @onHide={{@controller.hidePreviewEvent}}
          @onMouseEnter={{@controller.keepEventPreviewOpen}}
          @onMouseLeave={{@controller.scheduleEventPreviewDismiss}}
          @onNext={{@controller.showNextEventPreview}}
          @onPrevious={{@controller.showPreviousEventPreview}}
          @onRsvp={{@controller.rsvpPreviewEvent}}
          @onTouchEnd={{@controller.finishEventPreviewSwipe}}
          @onTouchStart={{@controller.startEventPreviewSwipe}}
          @positionLabel={{@controller.eventPreviewPositionLabel}}
          @showNavigation={{@controller.showEventPreviewNavigation}}
          @style={{@controller.eventPreviewStyle}}
          @topicUrl={{@controller.selectedPreviewTopicUrl}}
        />
      {{/if}}
    </section>

    {{#if @controller.canCreateEvents}}
      {{#if @controller.showCreatePanel}}
        <section
          class="discourse-events-calendar__event-composer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="event-composer-title"
          {{on "keydown" @controller.handleCreateDialogKeydown}}
        >
          <div
            class="discourse-events-calendar__event-composer-backdrop"
            role="presentation"
            aria-hidden="true"
            {{on "click" @controller.closeCreateForm}}
          ></div>

          <div
            class="discourse-events-calendar__event-composer-drawer"
            tabindex="-1"
            {{didInsert @controller.focusCreateDialog}}
          >
            <div class="discourse-events-calendar__create-header">
              <div>
                <span class="discourse-events-calendar__create-kicker">Event composer</span>
                <h2 id="event-composer-title">New event</h2>
              </div>
              <div class="discourse-events-calendar__create-header-actions">
                <button
                  type="submit"
                  form="event-create-form"
                  class="btn"
                  data-status="draft"
                  disabled={{@controller.cannotSubmitEvent}}
                >
                  {{#if @controller.isSaving}}
                    Saving...
                  {{else}}
                    Save draft
                  {{/if}}
                </button>
                <button
                  type="submit"
                  form="event-create-form"
                  class="btn btn-primary"
                  data-status="published"
                  disabled={{@controller.cannotSubmitEvent}}
                >
                  {{#if @controller.isSaving}}
                    Saving...
                  {{else}}
                    Publish event
                  {{/if}}
                </button>
                <button
                  type="button"
                  class="btn-flat discourse-events-calendar__event-composer-close"
                  aria-label="Close event form"
                  {{on "click" @controller.closeCreateForm}}
                >
                  <span class="discourse-events-calendar__event-composer-close-icon">&times;</span>
                  <span class="discourse-events-calendar__event-composer-close-label">Cancel</span>
                </button>
              </div>
            </div>

            <div class="discourse-events-calendar__event-composer-body">

              {{#if @controller.createNotice}}
                <div class="discourse-events-calendar__notice" role="status">
                  {{@controller.createNotice}}
                </div>
              {{/if}}

              {{#if @controller.isCreateFormExpanded}}
                <form id="event-create-form" {{on "submit" @controller.createEvent}}>
          <div class="discourse-events-calendar__field">
            <label for="event-title">Title</label>
            <Input
              id="event-title"
              @value={{@controller.title}}
              placeholder="Community meetup"
              required={{true}}
            />
          </div>

          <div class="discourse-events-calendar__field discourse-events-calendar__create-image-field">
            <label class="discourse-events-calendar__section-label" for="event-image-upload">Image</label>
            <div class="discourse-events-calendar__create-image-row">
              <div class="discourse-events-calendar__image-upload-control">
                <input
                  id="event-image-upload"
                  type="file"
                  accept="image/*"
                  disabled={{@controller.isUploadingImage}}
                  {{on "change" @controller.uploadEventImage}}
                />
                <p class="discourse-events-calendar__field-hint">
                  Optional cover image shown on the event detail page.
                </p>
              </div>

              {{#if @controller.canUseAiImageGeneration}}
                <div class="discourse-events-calendar__ai-image-tools">
                  {{#if @controller.showAiImagePrompt}}
                    <label for="event-ai-image-prompt">AI image prompt</label>
                    <textarea
                      id="event-ai-image-prompt"
                      value={{@controller.aiImagePrompt}}
                      rows="4"
                      {{on "input" @controller.updateAiImagePrompt}}
                    ></textarea>
                    <div class="discourse-events-calendar__inline-actions">
                      <button
                        type="button"
                        class="btn btn-primary btn-small"
                        disabled={{@controller.cannotGenerateAiImage}}
                        {{on "click" @controller.generateAiImage}}
                      >
                        {{#if @controller.isGeneratingAiImage}}
                          Generating...
                        {{else}}
                          Generate image
                        {{/if}}
                      </button>
                      <button
                        type="button"
                        class="btn btn-small"
                        disabled={{@controller.isGeneratingAiImage}}
                        {{on "click" @controller.resetAiImagePrompt}}
                      >
                        Reset from event details
                      </button>
                    </div>
                  {{else}}
                    <div class="discourse-events-calendar__ai-image-compact">
                      <button
                        type="button"
                        class="btn btn-small"
                        disabled={{@controller.cannotComposeAiImagePrompt}}
                        {{on "click" @controller.openAiImagePrompt}}
                      >
                        {{dIcon "wand-magic-sparkles"}}
                        {{@controller.aiImagePromptToggleLabel}}
                      </button>
                      {{#if @controller.hasEventImageUpload}}
                        <button
                          type="button"
                          class="btn btn-small"
                          disabled={{@controller.isGeneratingAiImage}}
                          {{on "click" @controller.regenerateAiImage}}
                        >
                          Regenerate
                        </button>
                      {{/if}}
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </div>
            {{#if @controller.hasPendingAiImageUpload}}
              <div class="discourse-events-calendar__ai-image-review">
                <div class="discourse-events-calendar__ai-image-review-header">
                  <div>
                    <strong>Generated image preview</strong>
                    <span>Review it before using it for this event.</span>
                  </div>
                </div>
                <img src={{@controller.pendingAiImagePreviewUrl}} alt="" />
                <div class="discourse-events-calendar__inline-actions">
                  <button type="button" class="btn btn-primary btn-small" {{on "click" @controller.acceptAiImage}}>
                    Use this image
                  </button>
                  <button
                    type="button"
                    class="btn btn-small"
                    disabled={{@controller.isGeneratingAiImage}}
                    {{on "click" @controller.regenerateAiImage}}
                  >
                    Regenerate
                  </button>
                  <button type="button" class="btn btn-small" {{on "click" @controller.openAiImagePrompt}}>
                    Edit prompt
                  </button>
                  <button type="button" class="btn btn-small" {{on "click" @controller.discardAiImage}}>
                    Discard
                  </button>
                </div>
              </div>
            {{/if}}
            {{#if @controller.hasEventImageUpload}}
              <div class="discourse-events-calendar__image-upload-preview discourse-events-calendar__image-upload-preview--cover">
                <img src={{@controller.eventImagePreviewUrl}} alt="" />
                <button type="button" class="btn btn-small" {{on "click" @controller.removeEventImage}}>
                  Remove image
                </button>
              </div>
            {{/if}}
            {{#if @controller.isUploadingImage}}
              <p class="discourse-events-calendar__field-hint">Uploading image...</p>
            {{/if}}
            {{#if @controller.imageUploadError}}
              <p class="discourse-events-calendar__form-error">{{@controller.imageUploadError}}</p>
            {{/if}}
          </div>

          <div class="discourse-events-calendar__participant-fields">
            <div class="discourse-events-calendar__field-label-row">
              <div>
                <h3>Event team</h3>
                <p class="discourse-events-calendar__field-hint">
                  Use @username to link a Discourse user, or enter a plain name.
                </p>
              </div>
              <button
                type="button"
                class="btn btn-small discourse-events-calendar__people-toggle"
                aria-expanded={{@controller.showCreateAdvancedPeopleFields}}
                {{on "click" @controller.toggleCreatePeopleDetails}}
              >
                {{@controller.createPeopleToggleLabel}}
                {{dIcon "chevron-down"}}
              </button>
            </div>

            <div class="discourse-events-calendar__field-row discourse-events-calendar__participant-row discourse-events-calendar__participant-row--primary">
              <div class="discourse-events-calendar__field">
                <label for="event-host-names">Host</label>
                <Input
                  id="event-host-names"
                  @value={{@controller.hostNames}}
                  placeholder="@sam, Ada Lovelace"
                  list="event-role-user-options"
                  {{on "input" @controller.updateHostNames}}
                />
              </div>
            </div>

            {{#if @controller.showCreateAdvancedPeopleFields}}
              <div class="discourse-events-calendar__field-row discourse-events-calendar__participant-row">
                <div class="discourse-events-calendar__field">
                  <label for="event-co-host-names">Co-hosts</label>
                  <Input
                    id="event-co-host-names"
                    @value={{@controller.coHostNames}}
                    placeholder="@riley, Operations team"
                    list="event-role-user-options"
                    {{on "input" @controller.updateCoHostNames}}
                  />
                </div>

                <div class="discourse-events-calendar__field">
                  <label for="event-speaker-names">Speakers</label>
                  <Input
                    id="event-speaker-names"
                    @value={{@controller.speakerNames}}
                    placeholder="@grace, Guest speaker"
                    list="event-role-user-options"
                    {{on "input" @controller.updateSpeakerNames}}
                  />
                </div>

                <div class="discourse-events-calendar__field">
                  <label for="event-with-names">Partners</label>
                  <Input
                    id="event-with-names"
                    @value={{@controller.withNames}}
                    placeholder="Local partner or collaborator"
                    list="event-role-user-options"
                    {{on "input" @controller.updateWithNames}}
                  />
                </div>
              </div>
            {{/if}}

            <datalist id="event-role-user-options">
              {{#each @controller.roleUserSuggestionOptions as |user|}}
                <option value={{user.value}} label={{user.label}}></option>
              {{/each}}
            </datalist>
            {{#if @controller.isLoadingRoleUsers}}
              <p class="discourse-events-calendar__field-hint" role="status">Searching users...</p>
            {{/if}}
          </div>

          <div class="discourse-events-calendar__date-builder discourse-events-calendar__date-builder--composer">
            <div class="discourse-events-calendar__schedule-fields">
              <div class="discourse-events-calendar__quick-dates" aria-label="Quick date choices">
                {{#each @controller.quickDates as |quickDate|}}
                  <button
                    type="button"
                    class="btn btn-small"
                    {{on "click" (fn @controller.selectQuickDate quickDate.date)}}
                  >
                    {{quickDate.label}}
                  </button>
                {{/each}}
              </div>

              <div class="discourse-events-calendar__schedule-compact-row">
                <div class="discourse-events-calendar__field discourse-events-calendar__schedule-date-field">
                  <label for="event-date">Date</label>
                  <Input
                    id="event-date"
                    @type="date"
                    @value={{@controller.eventDate}}
                    required={{true}}
                    {{on "input" @controller.updateEventDate}}
                    {{on "change" @controller.updateEventDate}}
                  />
                </div>

                  {{#unless @controller.allDay}}
                    <div class="discourse-events-calendar__field">
                      <label for="event-start-time">Start</label>
                      <select
                        id="event-start-time"
                        value={{@controller.startTime}}
                        {{on "input" @controller.updateStartTime}}
                        {{on "change" @controller.updateStartTime}}
                      >
                        {{#each @controller.startTimeOptions as |time|}}
                          <option value={{time.value}} selected={{time.isSelected}}>{{time.label}}</option>
                        {{/each}}
                      </select>
                    </div>

                    <div class="discourse-events-calendar__field">
                      <label for="event-end-time">End</label>
                      <select
                        id="event-end-time"
                        value={{@controller.endTime}}
                        {{on "input" @controller.updateEndTime}}
                        {{on "change" @controller.updateEndTime}}
                      >
                        {{#each @controller.endTimeOptions as |time|}}
                          <option value={{time.value}} selected={{time.isSelected}}>{{time.label}}</option>
                        {{/each}}
                      </select>
                    </div>
                  {{/unless}}

                  <div class="discourse-events-calendar__field discourse-events-calendar__schedule-timezone-field">
                    <label for="event-timezone">Timezone</label>
                    <select
                      id="event-timezone"
                      value={{@controller.timezone}}
                      {{on "change" @controller.updateTimezone}}
                    >
                      {{#each @controller.timezoneOptions as |timezone|}}
                        <option value={{timezone.value}} selected={{timezone.isSelected}}>{{timezone.label}}</option>
                      {{/each}}
                    </select>
                  </div>

                  <div class="discourse-events-calendar__schedule-toggles">
                    <label class="discourse-events-calendar__checkbox">
                      <input
                        type="checkbox"
                        checked={{@controller.allDay}}
                        {{on "change" @controller.toggleAllDay}}
                      />
                      <span>All day</span>
                    </label>

                    {{#unless @controller.allDay}}
                      <label class="discourse-events-calendar__checkbox">
                        <input
                          type="checkbox"
                          checked={{@controller.endsNextDay}}
                          {{on "change" @controller.toggleEndsNextDay}}
                        />
                        <span>Ends next day</span>
                        {{#if @controller.endsNextDay}}
                          <em>{{@controller.endDateLabel}}</em>
                        {{/if}}
                      </label>
                    {{/unless}}
                  </div>
                </div>

                {{#if @controller.allDay}}
                  <div class="discourse-events-calendar__field-hint">
                    This event will occupy the all-day row for {{@controller.eventDate}}.
                  </div>
                {{else}}
                  {{#if @controller.hasInvalidTimeRange}}
                    <div class="discourse-events-calendar__field-hint discourse-events-calendar__field-hint--error">
                      Choose a later end time, or mark the event as ending the next day.
                    </div>
                  {{/if}}
                {{/if}}

              <div class="discourse-events-calendar__field-row discourse-events-calendar__schedule-options-row">
                <div class="discourse-events-calendar__field">
                  <label for="event-recurrence-rule">Repeats</label>
                  <select
                    id="event-recurrence-rule"
                    value={{@controller.recurrenceRule}}
                    {{on "change" @controller.updateRecurrenceRule}}
                  >
                    <option value="none" selected={{@controller.isNoRecurrence}}>None</option>
                    <option value="daily" selected={{@controller.isDailyRecurrence}}>Daily</option>
                    <option value="weekly" selected={{@controller.isWeeklyRecurrence}}>Weekly</option>
                    <option value="monthly" selected={{@controller.isMonthlyRecurrence}}>Monthly</option>
                    <option value="yearly" selected={{@controller.isYearlyRecurrence}}>Yearly</option>
                  </select>
                </div>

                {{#if @controller.isRecurring}}
                  <div class="discourse-events-calendar__field">
                    <label for="event-recurrence-interval">Repeat interval</label>
                    <Input
                      id="event-recurrence-interval"
                      @type="number"
                      @value={{@controller.recurrenceInterval}}
                      min="1"
                      step="1"
                      {{on "change" @controller.updateRecurrenceInterval}}
                    />
                    <span class="discourse-events-calendar__field-hint">
                      {{@controller.recurrenceIntervalLabel}}
                    </span>
                  </div>

                  <div class="discourse-events-calendar__field">
                    <label for="event-recurrence-until">Repeat until</label>
                    <Input
                      id="event-recurrence-until"
                      @type="date"
                      @value={{@controller.recurrenceUntil}}
                      min={{@controller.recurrenceUntilMin}}
                      required={{true}}
                    />
                  </div>
                {{/if}}

                {{#if @controller.rsvpGloballyEnabled}}
                  <div class="discourse-events-calendar__field discourse-events-calendar__rsvp-config-field">
                    <label>RSVP</label>
                    <div class="discourse-events-calendar__rsvp-config">
                      <label class="discourse-events-calendar__checkbox">
                        <input
                          type="checkbox"
                          checked={{@controller.rsvpEnabled}}
                          {{on "change" @controller.toggleRsvpEnabled}}
                        />
                        <span>Enable</span>
                      </label>

                      {{#if @controller.rsvpEnabled}}
                        <select
                          aria-label="RSVP handling"
                          value={{@controller.rsvpMode}}
                          {{on "change" @controller.updateRsvpMode}}
                        >
                          <option value="native" selected={{@controller.isNativeRsvpMode}}>Built-in</option>
                          <option value="external" selected={{@controller.isExternalRsvpMode}}>External link</option>
                        </select>

                        {{#if @controller.isNativeRsvpMode}}
                          <label class="discourse-events-calendar__checkbox discourse-events-calendar__rsvp-inline-option">
                            <input
                              type="checkbox"
                              checked={{@controller.rsvpAllowInterested}}
                              {{on "change" @controller.toggleRsvpAllowInterested}}
                            />
                            <span>Allow interested</span>
                          </label>
                        {{/if}}
                      {{/if}}
                    </div>
                  </div>
                {{/if}}

                <div class="discourse-events-calendar__field discourse-events-calendar__capacity-field">
                  <label for="event-capacity">Capacity</label>
                  <Input
                    id="event-capacity"
                    @type="number"
                    @value={{@controller.capacity}}
                    min="1"
                    placeholder="Unlimited"
                    {{on "input" @controller.updateCapacity}}
                  />
                </div>
              </div>

              {{#if @controller.rsvpGloballyEnabled}}
                {{#if @controller.rsvpEnabled}}
                {{#if @controller.isExternalRsvpMode}}
                  <div class="discourse-events-calendar__field discourse-events-calendar__external-rsvp-field">
                    <label for="event-external-rsvp-url">External RSVP URL</label>
                    <Input
                      id="event-external-rsvp-url"
                      @value={{@controller.externalRsvpUrl}}
                      placeholder="https://example.com/events/..."
                      {{on "input" @controller.updateExternalRsvpUrl}}
                    />
                  </div>
                {{/if}}
                {{/if}}
              {{/if}}

              {{#if @controller.isWeeklyRecurrence}}
                <fieldset class="discourse-events-calendar__weekday-picker">
                  <legend>Repeat on</legend>
                  {{#each @controller.recurrenceWeekdayOptions as |weekday|}}
                    <label>
                      <input
                        type="checkbox"
                        checked={{weekday.isSelected}}
                        {{on "change" (fn @controller.toggleRecurrenceByday weekday)}}
                      />
                      <span>{{weekday.label}}</span>
                    </label>
                  {{/each}}
                </fieldset>
              {{/if}}

              {{#if @controller.hasInvalidRecurrenceRange}}
                <div class="discourse-events-calendar__field-hint discourse-events-calendar__field-hint--error">
                  Choose a repeat-until date on or after the event date.
                </div>
              {{/if}}

              <div class="discourse-events-calendar__venue-section" aria-labelledby="event-venue-heading">
                <div class="discourse-events-calendar__venue-section-heading">
                  <h3 id="event-venue-heading">Venue</h3>
                  <div class="discourse-events-calendar__venue-actions">
                    {{#if @controller.isExistingVenueType}}
                      {{#if @controller.canUseVenueFinder}}
                        <button
                          type="button"
                          class="btn btn-small discourse-events-calendar__venue-finder-trigger"
                          aria-expanded={{@controller.showVenueFinder}}
                          disabled={{@controller.isLoadingVenueSuggestions}}
                          {{on "click" @controller.toggleVenueFinder}}
                        >
                          {{dIcon "wand-magic-sparkles"}}
                          <span>{{@controller.venueFinderButtonLabel}}</span>
                        </button>
                      {{/if}}

                      {{#if @controller.placesEnabled}}
                        <button
                          type="button"
                          class="btn btn-small discourse-events-calendar__venue-search-trigger"
                          aria-expanded={{@controller.showVenueSearch}}
                          {{on "click" @controller.toggleVenueSearch}}
                        >
                          {{dIcon "magnifying-glass"}}
                          <span>{{@controller.venueSearchButtonLabel}}</span>
                        </button>
                      {{/if}}
                    {{/if}}
                  </div>
                </div>

                <div class="discourse-events-calendar__field-row discourse-events-calendar__venue-type-row">
                  <div class="discourse-events-calendar__field discourse-events-calendar__venue-type-field">
                    <label for="event-venue-type">Venue type</label>
                    <select
                      id="event-venue-type"
                      value={{@controller.venueType}}
                      {{on "change" @controller.updateVenueType}}
                    >
                      <option value="tbd" selected={{@controller.isVenueTbdType}}>TBD</option>
                      <option value="existing" selected={{@controller.isExistingVenueType}}>Venue</option>
                      <option value="custom" selected={{@controller.isCustomVenueType}}>Custom location</option>
                      <option value="virtual" selected={{@controller.isVirtualVenueType}}>Virtual</option>
                    </select>
                  </div>

                  {{#if @controller.isExistingVenueType}}
                    {{#if @controller.hasLocationOptions}}
                      <div class="discourse-events-calendar__field">
                        <label for="event-location-choice">Venue</label>
                        <select
                          id="event-location-choice"
                          value={{@controller.selectedLocationId}}
                          {{on "change" @controller.selectLocation}}
                        >
                          <option value="" selected={{@controller.hasNoSelectedLocation}}>Choose venue</option>
                          {{#each @controller.locationOptions as |location|}}
                            <option value={{location.value}} selected={{location.isSelected}}>{{location.name}}</option>
                          {{/each}}
                        </select>
                      </div>
                    {{else if @controller.hasLocations}}
                      <div class="discourse-events-calendar__venue-state-panel">
                        <strong>No venues match this category</strong>
                        <span>Choose another category or search places.</span>
                      </div>
                    {{/if}}

                    {{#if @controller.hasRooms}}
                      <div class="discourse-events-calendar__field">
                        <label for="event-room">Room</label>
                        <select
                          id="event-room"
                          value={{@controller.selectedRoomId}}
                          {{on "change" @controller.selectRoom}}
                        >
                          <option value="" selected={{@controller.hasNoSelectedRoom}}>No room selected</option>
                          {{#each @controller.reservableRoomOptions as |room|}}
                            <option value={{room.id}} selected={{room.isSelected}}>{{room.name}}</option>
                          {{/each}}
                        </select>
                      </div>
                    {{/if}}
                  {{/if}}

                  {{#if @controller.isVenueTbdType}}
                    <div class="discourse-events-calendar__venue-state-panel">
                      <strong>Venue to be determined</strong>
                      <span>You can publish now and add venue details later.</span>
                    </div>
                  {{/if}}

                  {{#if @controller.isVirtualVenueType}}
                    <div class="discourse-events-calendar__field discourse-events-calendar__venue-detail-field">
                      <label for="event-meeting-url">Virtual event URL</label>
                      <Input
                        id="event-meeting-url"
                        @value={{@controller.meetingUrl}}
                        placeholder="https://meet.example.com/event"
                        {{on "input" @controller.updateMeetingUrl}}
                      />
                      <p class="discourse-events-calendar__field-hint">
                        This will be shown as the event venue link.
                      </p>
                    </div>
                  {{/if}}

                  {{#if @controller.isCustomVenueType}}
                    <div class="discourse-events-calendar__field discourse-events-calendar__venue-detail-field">
                      <label for="event-location">Custom location</label>
                      <Input
                        id="event-location"
                        @value={{@controller.location}}
                        placeholder="Venue name, address, map link, or meeting point"
                        {{on "input" @controller.updateCustomLocation}}
                      />
                      <p class="discourse-events-calendar__field-hint">
                        Use this when the venue is not in the curated list.
                      </p>
                    </div>
                  {{/if}}
                </div>

                {{#if @controller.isExistingVenueType}}
                  {{#if @controller.placesEnabled}}
                    {{#if @controller.showVenueSearch}}
                      <div class="discourse-events-calendar__venue-search-panel">
                        <div class="discourse-events-calendar__place-picker">
                          <Input
                            id="event-place-query"
                            @value={{@controller.placeQuery}}
                            placeholder="Search places"
                            autocomplete="off"
                            {{on "input" @controller.updatePlaceQuery}}
                          />
                          {{#if @controller.hasPlaceOptions}}
                            <div class="discourse-events-calendar__place-options" role="listbox" aria-label="Matching places">
                              {{#each @controller.placeOptions as |place|}}
                                <button
                                  type="button"
                                  class="btn-flat discourse-events-calendar__place-option"
                                  role="option"
                                  {{on "click" (fn @controller.selectPlace place)}}
                                >
                                  <strong>{{place.name}}</strong>
                                  {{#if place.address}}
                                    <span>{{place.address}}</span>
                                  {{/if}}
                                </button>
                              {{/each}}
                            </div>
                          {{/if}}
                        </div>
                        {{#if @controller.isLoadingPlaces}}
                          <p class="discourse-events-calendar__field-hint">Searching places...</p>
                        {{/if}}
                      </div>
                    {{/if}}

                    {{#if @controller.selectedPlaceSummary}}
                      <div class="discourse-events-calendar__selected-place-pill">
                        <span>{{@controller.selectedPlaceSummary}}</span>
                        <button type="button" class="btn-flat" {{on "click" @controller.clearPlace}}>
                          Clear
                        </button>
                      </div>
                    {{/if}}
                  {{/if}}

                  {{#if @controller.hasRoomAvailabilityStatus}}
                    <div class={{@controller.roomAvailabilityClass}} role="status">
                      <div>{{@controller.roomAvailabilityMessage}}</div>
                      {{#if @controller.roomAvailabilityConflict}}
                        <div class="discourse-events-calendar__conflict-card">
                          <span class="discourse-events-calendar__conflict-kicker">
                            Conflicting reservation
                          </span>
                          <strong>{{@controller.roomAvailabilityConflictTitle}}</strong>
                          <span>{{@controller.roomAvailabilityConflictRange}}</span>
                          {{#if @controller.hasRoomAvailabilityConflictEventPath}}
                            <a
                              class="btn btn-small"
                              href={{@controller.roomAvailabilityConflictEventPath}}
                            >
                              View event
                            </a>
                          {{/if}}
                        </div>
                      {{/if}}
                    </div>
                  {{/if}}

                  {{#if @controller.showVenueFinder}}
                    <div class="discourse-events-calendar__venue-finder-panel" role="region" aria-label="Suggested venues">
                      <div class="discourse-events-calendar__venue-finder-header">
                        <div>
                          <strong>Suggested venues</strong>
                          {{#if @controller.venueFinderSummary}}
                            <span>{{@controller.venueFinderSummary}}</span>
                          {{/if}}
                        </div>
                        <div class="discourse-events-calendar__venue-finder-actions">
                          <button
                            type="button"
                            class="btn btn-small"
                            disabled={{@controller.isLoadingVenueSuggestions}}
                            {{on "click" @controller.refreshVenueSuggestions}}
                          >
                            Refresh
                          </button>
                          <button
                            type="button"
                            class="btn-flat discourse-events-calendar__venue-finder-close"
                            aria-label="Close venue suggestions"
                            {{on "click" @controller.closeVenueFinder}}
                          >
                            &times;
                          </button>
                        </div>
                      </div>

                      {{#if @controller.isLoadingVenueSuggestions}}
                        <div class="discourse-events-calendar__venue-finder-status" role="status">
                          <span class="discourse-events-calendar__month-more-loading-spinner"></span>
                          Checking available venues...
                        </div>
                      {{else if @controller.hasVenueFinderMissingFields}}
                        <p class="discourse-events-calendar__venue-finder-empty">
                          {{@controller.venueFinderMissingText}}
                        </p>
                      {{else if @controller.venueFinderError}}
                        <p class="discourse-events-calendar__form-error">
                          {{@controller.venueFinderError}}
                        </p>
                      {{else if @controller.hasVenueSuggestions}}
                        <div class="discourse-events-calendar__venue-finder-results">
                          {{#each @controller.venueSuggestions as |suggestion|}}
                            <button
                              type="button"
                              class="discourse-events-calendar__venue-finder-result"
                              {{on "click" (fn @controller.applyVenueSuggestion suggestion)}}
                            >
                              <span class="discourse-events-calendar__venue-finder-thumb">
                                {{#if suggestion.imageUrl}}
                                  <img src={{suggestion.imageUrl}} alt="" loading="lazy" decoding="async" />
                                {{else}}
                                  {{dIcon "location-dot"}}
                                {{/if}}
                              </span>
                              <span class="discourse-events-calendar__venue-finder-result-main">
                                <strong>{{suggestion.title}}</strong>
                                {{#if suggestion.subtitle}}
                                  <span>{{suggestion.subtitle}}</span>
                                {{/if}}
                                <em>{{suggestion.primaryReason}}</em>
                              </span>
                              <span class="discourse-events-calendar__venue-finder-result-meta">
                                <span>{{suggestion.availabilityLabel}}</span>
                                <span>{{suggestion.capacityLabel}}</span>
                              </span>
                            </button>
                          {{/each}}
                        </div>
                      {{else}}
                        <p class="discourse-events-calendar__venue-finder-empty">
                          No matching available venues. Try another time or capacity.
                        </p>
                      {{/if}}
                    </div>
                  {{/if}}
                {{/if}}
              </div>

              <div class="discourse-events-calendar__field-row discourse-events-calendar__meta-row">
                <div class="discourse-events-calendar__field">
                  <label for="event-visibility">Visibility</label>
                  <select
                    id="event-visibility"
                    value={{@controller.visibility}}
                    {{on "change" @controller.updateVisibility}}
                  >
                    <option value="public" selected={{@controller.isPublicVisibility}}>Public</option>
                    <option value="unlisted" selected={{@controller.isUnlistedVisibility}}>Unlisted</option>
                    <option value="staff" selected={{@controller.isStaffVisibility}}>Staff only</option>
                    <option value="private" selected={{@controller.isPrivateVisibility}}>Private invite-only</option>
                  </select>
                </div>

                <div class="discourse-events-calendar__field">
                  <label for="event-category">Category</label>
                  <select
                    id="event-category"
                    value={{@controller.selectedCategoryId}}
                    {{on "change" @controller.updateCategory}}
                  >
                    <option value="" selected={{@controller.hasNoCategorySelected}}>Site default</option>
                    {{#each @controller.categoryOptions as |category|}}
                      <option value={{category.id}} selected={{category.isSelected}}>{{category.name}}</option>
                    {{/each}}
                  </select>
                </div>

                <div class="discourse-events-calendar__field discourse-events-calendar__create-tags-field">
                  <label id="event-tags-label">Tags</label>
                  <div class="discourse-events-calendar__create-tags">
                    <button
                      type="button"
                      class="discourse-events-calendar__tag-filter-trigger"
                      aria-labelledby="event-tags-label"
                      aria-haspopup="true"
                      aria-expanded={{@controller.isCreateTagMenuOpen}}
                      {{on "click" @controller.toggleCreateTagMenu}}
                    >
                      <span>{{@controller.createTagSummaryLabel}}</span>
                    </button>

                    {{#if @controller.isCreateTagMenuOpen}}
                      <div class="discourse-events-calendar__tag-filter-menu discourse-events-calendar__create-tag-menu" role="group" aria-label="Event tags">
                        <div class="discourse-events-calendar__tag-filter-menu-header">
                          <span>Existing tags</span>
                        </div>

                        {{#if @controller.hasAvailableTags}}
                          <div class="discourse-events-calendar__tag-filter-options discourse-events-calendar__create-tag-options">
                            {{#each @controller.createTagOptions as |tag|}}
                              <label class="discourse-events-calendar__tag-filter-option discourse-events-calendar__create-tag-option">
                                <input
                                  type="checkbox"
                                  class="discourse-events-calendar__create-tag-option-check"
                                  checked={{tag.isSelected}}
                                  {{on "change" (fn @controller.toggleCreateTag tag.id)}}
                                />
                                <span class="discourse-events-calendar__tag-filter-name discourse-events-calendar__create-tag-option-name">{{tag.name}}</span>
                              </label>
                            {{/each}}
                          </div>
                        {{else}}
                          <p class="discourse-events-calendar__field-hint">No existing tags yet.</p>
                        {{/if}}

                        <div class="discourse-events-calendar__create-tag-new">
                          <label for="event-new-tag">Add tag</label>
                          <div class="discourse-events-calendar__create-tag-new-row">
                            <Input
                              id="event-new-tag"
                              @value={{@controller.newTagName}}
                              placeholder="workshop"
                              list="event-existing-tags"
                              {{on "input" @controller.updateNewTagName}}
                              {{on "keydown" @controller.handleNewTagKeydown}}
                            />
                            <button
                              type="button"
                              class="btn btn-small"
                              disabled={{@controller.cannotAddCreateTag}}
                              {{on "click" @controller.addCreateTag}}
                            >
                              Add
                            </button>
                          </div>
                          <datalist id="event-existing-tags">
                            {{#each @controller.createTagOptions as |tag|}}
                              <option value={{tag.name}}></option>
                            {{/each}}
                          </datalist>
                        </div>
                      </div>
                    {{/if}}

                    {{#if @controller.hasCreateTagSelections}}
                      <div class="discourse-events-calendar__create-tag-chips" aria-label="Selected event tags">
                        {{#each @controller.selectedCreateTags as |tag|}}
                          <span class="discourse-events-calendar__create-tag-chip">
                            {{tag.name}}
                            <button
                              type="button"
                              aria-label="Remove {{tag.name}}"
                              {{on "click" (fn @controller.removeCreateTag tag.id)}}
                            >
                              &times;
                            </button>
                          </span>
                        {{/each}}
                        {{#each @controller.pendingCreateTagNames as |tagName|}}
                          <span class="discourse-events-calendar__create-tag-chip discourse-events-calendar__create-tag-chip--new">
                            {{tagName}}
                            <button
                              type="button"
                              aria-label="Remove {{tagName}}"
                              {{on "click" (fn @controller.removePendingCreateTag tagName)}}
                            >
                              &times;
                            </button>
                          </span>
                        {{/each}}
                      </div>
                    {{/if}}
                  </div>
                </div>

                <div class="discourse-events-calendar__field discourse-events-calendar__conversation-field">
                  <label for="event-conversation-mode">Conversation</label>
                  <select
                    id="event-conversation-mode"
                    value={{@controller.conversationMode}}
                    {{on "change" @controller.updateConversationMode}}
                  >
                    <option value="none" selected={{@controller.isNoConversation}}>None</option>
                    <option value="discussion" selected={{@controller.isDiscussionConversation}}>Discussion topic</option>
                    {{#if @controller.canUseChatThread}}
                      <option value="chat" selected={{@controller.isChatConversation}}>Chat thread</option>
                    {{/if}}
                  </select>
                </div>
              </div>

              {{#if @controller.showInviteesSection}}
                <div class="discourse-events-calendar__invitees-editor">
                  <h3>Invitees</h3>
                  <div class="discourse-events-calendar__field-row">
                    <div class="discourse-events-calendar__field">
                      <label for="event-invite-usernames">Invite users</label>
                      <Input
                        id="event-invite-usernames"
                        @value={{@controller.inviteUsernames}}
                        placeholder="alice, @sam"
                        {{on "input" @controller.updateInviteUsernames}}
                      />
                    </div>

                    <div class="discourse-events-calendar__field">
                      <label for="event-invite-groups">Invite groups</label>
                      <Input
                        id="event-invite-groups"
                        @value={{@controller.inviteGroupNames}}
                        placeholder="staff, residents"
                        {{on "input" @controller.updateInviteGroupNames}}
                      />
                    </div>
                  </div>
                </div>
              {{/if}}

            </div>

          </div>

          {{#if @controller.showRoomForm}}
            <div class="discourse-events-calendar__inline-room-form">
              <div class="discourse-events-calendar__field-row">
                <div class="discourse-events-calendar__field">
                  <label for="room-name">Room name</label>
                  <Input
                    id="room-name"
                    @value={{@controller.roomName}}
                    placeholder="Studio"
                  />
                </div>

                <div class="discourse-events-calendar__field">
                  <label for="room-capacity">Capacity</label>
                  <Input
                    id="room-capacity"
                    @type="number"
                    @value={{@controller.roomCapacity}}
                    min="1"
                  />
                </div>
              </div>

              <div class="discourse-events-calendar__field">
                <label for="room-location">Venue for this room</label>
                <Input
                  id="room-location"
                  @value={{@controller.roomLocation}}
                  placeholder="Main Hall"
                />
                <p class="discourse-events-calendar__field-hint">
                  Use the venue name; the event address stays editable below.
                </p>
              </div>

              <div class="discourse-events-calendar__field">
                <label for="room-image-upload">Room image</label>
                <input
                  id="room-image-upload"
                  type="file"
                  accept="image/*"
                  disabled={{@controller.isUploadingRoomImage}}
                  {{on "change" @controller.uploadRoomImage}}
                />
                {{#if @controller.hasRoomImageUpload}}
                  <div class="discourse-events-calendar__image-upload-preview">
                    <img src={{@controller.roomImagePreviewUrl}} alt="" />
                    <button type="button" class="btn btn-small" {{on "click" @controller.removeRoomImage}}>
                      Remove image
                    </button>
                  </div>
                {{/if}}
                {{#if @controller.isUploadingRoomImage}}
                  <p class="discourse-events-calendar__field-hint">Uploading image...</p>
                {{/if}}
                {{#if @controller.roomImageUploadError}}
                  <p class="discourse-events-calendar__form-error">{{@controller.roomImageUploadError}}</p>
                {{/if}}
              </div>

              <div class="discourse-events-calendar__field-row">
                <div class="discourse-events-calendar__field">
                  <label for="room-booking-window-days">Booking window</label>
                  <Input
                    id="room-booking-window-days"
                    @type="number"
                    @value={{@controller.roomBookingWindowDays}}
                    min="0"
                    placeholder="No limit"
                  />
                  <p class="discourse-events-calendar__field-hint">
                    Maximum days in advance this room can be reserved.
                  </p>
                </div>

                <div class="discourse-events-calendar__field">
                  <label for="room-buffer-minutes">Buffer</label>
                  <Input
                    id="room-buffer-minutes"
                    @type="number"
                    @value={{@controller.roomBufferMinutes}}
                    min="0"
                    step="5"
                    placeholder="0"
                  />
                  <p class="discourse-events-calendar__field-hint">
                    Minutes blocked before and after reservations.
                  </p>
                </div>
              </div>

              {{#if @controller.roomError}}
                <div class="discourse-events-calendar__error" role="alert">
                  {{@controller.roomError}}
                </div>
              {{/if}}

              <div class="discourse-events-calendar__inline-actions">
                <button
                  type="button"
                  class="btn btn-primary"
                  disabled={{@controller.cannotCreateRoom}}
                  {{on "click" @controller.createRoom}}
                >
                  {{#if @controller.isSavingRoom}}
                    Saving...
                  {{else}}
                    Save room
                  {{/if}}
                </button>
                <button type="button" class="btn" {{on "click" @controller.cancelRoomForm}}>
                  Cancel
                </button>
              </div>
            </div>
          {{/if}}

          <div
            class="discourse-events-calendar__field discourse-events-calendar__description-field"
            {{didInsert @controller.setupEventDescriptionUpload}}
            {{willDestroy @controller.teardownEventDescriptionUpload}}
          >
            <div class="discourse-events-calendar__field-label-row">
              <label for="event-description">Description</label>
            </div>
            <DEditor
              @textAreaId="event-description"
              @value={{@controller.description}}
              @change={{@controller.updateDescription}}
              @placeholder="Share the agenda, links, images, or details attendees need."
              @extraButtons={{@controller.eventDescriptionExtraButtons}}
              @onSetup={{@controller.setupEventDescriptionEditor}}
              @categoryId={{@controller.selectedCategoryId}}
              class="discourse-events-calendar__description-editor"
            />
            <PickFilesButton
              @fileInputId={{@controller.descriptionFileUploadElementId}}
              @allowMultiple={{true}}
              @registerFileInput={{@controller.registerEventDescriptionFileInput}}
              name="event-description-file-uploader"
            />
          </div>

          {{#if @controller.createError}}
            <div class="discourse-events-calendar__error" role="alert">
              {{@controller.createError}}
              {{#if @controller.createConflict}}
                <div class="discourse-events-calendar__conflict-card discourse-events-calendar__conflict-card--error">
                  <span class="discourse-events-calendar__conflict-kicker">
                    Conflicting reservation
                  </span>
                  <strong>{{@controller.createConflictTitle}}</strong>
                  <span>{{@controller.createConflictRange}}</span>
                  {{#if @controller.hasCreateConflictEventPath}}
                    <a
                      class="btn btn-small"
                      href={{@controller.createConflictEventPath}}
                    >
                      View event
                    </a>
                  {{/if}}
                  {{#if @controller.hasCreateConflictAlternatives}}
                    <div class="discourse-events-calendar__conflict-alternatives">
                      <span>Available rooms at this time</span>
                      <ul>
                        {{#each @controller.createConflictAlternatives as |alternative|}}
                          <li>{{alternative.name}}</li>
                        {{/each}}
                      </ul>
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/if}}

                </form>
              {{/if}}
            </div>
          </div>
        </section>
      {{/if}}
    {{else}}
      <section class="discourse-events-calendar__panel discourse-events-calendar__signed-out">
        <h2>Create and RSVP</h2>
        <p>Log in to create events, reserve rooms, and RSVP.</p>
      </section>
    {{/if}}

  </div>
</template>
